import { useState, useEffect, useRef, useCallback } from 'react';

interface UseDynamicScrollSectionOptions {
  rootEl: HTMLElement | null;
  sectionIds: string[];
  scrollOffset?: number;
}

export const useDynamicScrollSection = ({
  rootEl,
  sectionIds,
  scrollOffset = 20,
}: UseDynamicScrollSectionOptions) => {
  const [activeSection, setActiveSection] = useState<string | null>(sectionIds[0] || null);

  const scrollingRef = useRef(false);
  const unlockTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!rootEl || sectionIds.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (scrollingRef.current) return;
        if (rootEl.scrollTop <= 2) {
          setActiveSection(sectionIds[0]);
          return;
        }

        let maxEntry: IntersectionObserverEntry | undefined;

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!maxEntry || entry.intersectionRatio > maxEntry.intersectionRatio) {
              maxEntry = entry;
            }
          }
        });

        if (maxEntry) {
          const id = (maxEntry.target as HTMLElement).id.replace('section-', '');
          setActiveSection(id);
        }
      },
      {
        root: rootEl,
        threshold: [0.1, 0.3, 0.6],
        rootMargin: '0px 0px -40% 0px',
      },
    );

    // Attach observer to each section node
    sectionIds.forEach((id) => {
      const el = document.getElementById(`section-${id}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [rootEl, sectionIds]);

  const scrollToSection = useCallback(
    (sectionId: string) => {
      if (!rootEl) return;

      const section = document.getElementById(`section-${sectionId}`);
      if (!section) return;

      scrollingRef.current = true;
      window.clearTimeout(unlockTimer.current);

      setActiveSection(sectionId);

      const rect = section.getBoundingClientRect();
      const rootRect = rootEl.getBoundingClientRect();
      const top = rect.top - rootRect.top + rootEl.scrollTop - scrollOffset;

      rootEl.scrollTo({ top, behavior: 'smooth' });

      let lastPosition = rootEl.scrollTop;

      const checkScrollEnd = () => {
        const current = rootEl.scrollTop;

        if (Math.abs(current - lastPosition) < 2) {
          scrollingRef.current = false;
          return;
        }

        lastPosition = current;
        unlockTimer.current = window.setTimeout(checkScrollEnd, 80);
      };

      unlockTimer.current = window.setTimeout(checkScrollEnd, 80);
    },
    [rootEl, scrollOffset],
  );

  return { activeSection, scrollToSection };
};
